import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle } from "lucide-react";

const LEVELS = [
  { code: "L1", label: "L1 · Agent" },
  { code: "L2", label: "L2 · Supervisor" },
  { code: "L3", label: "L3 · Manager" },
  { code: "L4", label: "L4 · Country Head" },
];

const LEVEL_COLORS = { L1: "bg-emerald-500", L2: "bg-amber-500", L3: "bg-orange-500", L4: "bg-rose-500" };

export default function Escalation() {
  const [tab, setTab] = useState("L2");
  const [data, setData] = useState({ rows: [], counts: {} });

  const load = useCallback(async (lvl) => {
    const r = await api.get(`/crm/escalation/tickets?level=${lvl}`);
    setData(r.data);
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  return (
    <div className="space-y-6" data-testid="escalation-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-rose-500" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold">Escalation board</h1>
          <p className="text-sm text-muted-foreground">4-level escalation flow: Agent → Supervisor → Manager → Country Head</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="escalation-tabs">
          {LEVELS.map(l => (
            <TabsTrigger key={l.code} value={l.code} data-testid={`tab-${l.code}`}>
              {l.label}
              {data.counts[l.code] > 0 && <span className="ml-2 text-xs opacity-70">({data.counts[l.code]})</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        {LEVELS.map(l => (
          <TabsContent key={l.code} value={l.code}>
            <Card>
              <CardHeader><CardTitle>{l.label} tickets</CardTitle></CardHeader>
              <CardContent>
                {data.rows.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nothing escalated here.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-muted-foreground text-left"><th className="p-2">Subject</th><th>Priority</th><th>Level</th><th>Reason</th><th>Escalated</th></tr></thead>
                    <tbody>
                      {data.rows.map(t => (
                        <tr key={t.id} className="border-t border-border hover:bg-muted/50">
                          <td className="p-2">
                            <Link to={`/app/crm/tickets/${t.id}`} className="hover:underline">{t.subject}</Link>
                          </td>
                          <td><Badge variant="outline" className="capitalize">{t.priority}</Badge></td>
                          <td><Badge className={`${LEVEL_COLORS[t.escalation_level]} text-white`}>{t.escalation_level}</Badge></td>
                          <td className="text-xs text-muted-foreground">{t.escalation_reason || "—"}</td>
                          <td className="text-xs">{t.escalated_at ? new Date(t.escalated_at).toLocaleString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
